import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { newsApi } from '../lib/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, Upload, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const NewsForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    excerpt: '',
    excerpt_ar: '',
    content: '',
    content_ar: '',
    image: '',
    published_at: '',
    status: 'draft',
    type: 'paragraph',
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [isUploading, setIsUploading] = useState(false);

  const { data: newsItem, isLoading } = useQuery({
    queryKey: ['news', id],
    queryFn: () => newsApi.getById(id!).then((res: any) => res.data),
    enabled: isEdit,
  });

  useEffect(() => {
    if (newsItem && isEdit) {
      setFormData({
        title: newsItem.title || '',
        title_ar: newsItem.title_ar || '',
        excerpt: newsItem.excerpt || '',
        excerpt_ar: newsItem.excerpt_ar || '',
        content: newsItem.content || '',
        content_ar: newsItem.content_ar || '',
        image: newsItem.image || '',
        published_at: newsItem.published_at ? newsItem.published_at.split('T')[0] : '',
        status: newsItem.status || 'draft',
        type: newsItem.type || 'paragraph',
      });
      
      // Set image preview if image exists
      if (newsItem.image) {
        setImagePreview(newsItem.image);
      }
    }
  }, [newsItem, isEdit]);

  const createMutation = useMutation({
    mutationFn: (data: any) => newsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      toast.success('News created successfully');
      navigate('/news');
    },
    onError: () => {
      toast.error('Failed to create news');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => newsApi.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      toast.success('News updated successfully');
      navigate('/news');
    },
    onError: () => {
      toast.error('Failed to update news');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isEdit) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageUpload = async (file: File) => {
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      
      // Upload to your Laravel backend
      const response = await fetch('http://127.0.0.1:8000/upload-image', {
        method: 'POST',
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({ ...prev, image: data.url }));
        toast.success('Image uploaded successfully');
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      toast.error('Failed to upload image');
      console.error('Upload error:', error);
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      // Upload the file
      handleImageUpload(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview('');
    setFormData(prev => ({ ...prev, image: '' }));
  };

  if (isLoading && isEdit) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/news')}
          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isEdit ? t('edit') : t('create')} {t('news')}
          </h1>
          <p className="mt-2 text-gray-600">
            {isEdit ? 'Update news article' : 'Create a new news article'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={`space-y-6 ${i18n.language === 'ar' ? 'rtl' : 'ltr'}`}>
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="title">
                  {t('newsTitle')} (English)
                </Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="title_ar">
                  {t('newsTitleAr')} (Arabic)
                </Label>
                <Input
                  id="title_ar"
                  name="title_ar"
                  value={formData.title_ar}
                  onChange={handleChange}
                  required
                  dir="rtl"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="excerpt">
                  {t('newsExcerpt')} (English)
                </Label>
                <Textarea
                  id="excerpt"
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleChange}
                  rows={3}
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="excerpt_ar">
                  {t('newsExcerptAr')} (Arabic)
                </Label>
                <Textarea
                  id="excerpt_ar"
                  name="excerpt_ar"
                  value={formData.excerpt_ar}
                  onChange={handleChange}
                  rows={3}
                  dir="rtl"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="content">
                  {t('newsContent')} (English)
                </Label>
                <Textarea
                  id="content"
                  name="content"
                  value={formData.content}
                  onChange={handleChange}
                  rows={6}
                  dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="content_ar">
                  {t('newsContentAr')} (Arabic)
                </Label>
                <Textarea
                  id="content_ar"
                  name="content_ar"
                  value={formData.content_ar}
                  onChange={handleChange}
                  rows={6}
                  dir="rtl"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Additional Settings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="image">{t('image')}</Label>
                
                {/* Image Upload Area */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                  {imagePreview ? (
                    <div className="relative">
                      <img 
                        src={imagePreview} 
                        alt="Preview" 
                        className="max-h-48 mx-auto rounded-lg"
                      />
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={removeImage}
                        className="absolute top-2 right-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-2">
                        {i18n.language === 'ar' ? 'اضغط لرفع صورة' : 'Click to upload image'}
                      </p>
                      <p className="text-sm text-gray-500">
                        {i18n.language === 'ar' ? 'PNG, JPG, GIF حتى 10MB' : 'PNG, JPG, GIF up to 10MB'}
                      </p>
                    </div>
                  )}
                  
                  <input
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                    disabled={isUploading}
                  />
                  
                  {!imagePreview && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('image')?.click()}
                      disabled={isUploading}
                      className="mt-4"
                    >
                      {isUploading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2"></div>
                          {i18n.language === 'ar' ? 'جاري الرفع...' : 'Uploading...'}
                        </>
                      ) : (
                        <>
                          <Upload className="h-4 w-4 mr-2" />
                          {i18n.language === 'ar' ? 'اختر صورة' : 'Choose Image'}
                        </>
                      )}
                    </Button>
                  )}
                </div>
                
                {/* Fallback URL input */}
                <div className="mt-4">
                  <Label htmlFor="image_url" className="text-sm text-gray-500">
                    {i18n.language === 'ar' ? 'أو أدخل رابط الصورة مباشرة:' : 'Or enter image URL directly:'}
                  </Label>
                  <Input
                    id="image_url"
                    type="url"
                    name="image"
                    value={formData.image}
                    onChange={handleChange}
                    placeholder={i18n.language === 'ar' ? 'رابط الصورة' : 'Image URL'}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="published_at">{t('publishedAt')}</Label>
                <Input
                  id="published_at"
                  type="date"
                  name="published_at"
                  value={formData.published_at}
                  onChange={handleChange}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="status">{t('status')}</Label>
                <Select name="status" value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                  <SelectTrigger dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                    <SelectValue placeholder={i18n.language === 'ar' ? 'اختر الحالة' : 'Select status'} />
                  </SelectTrigger>
                  <SelectContent dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                    <SelectItem value="draft">{i18n.language === 'ar' ? 'مسودة' : 'Draft'}</SelectItem>
                    <SelectItem value="published">{i18n.language === 'ar' ? 'منشور' : 'Published'}</SelectItem>
                    <SelectItem value="archived">{i18n.language === 'ar' ? 'مؤرشف' : 'Archived'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="type">{t('type')}</Label>
                <Select name="type" value={formData.type} onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}>
                  <SelectTrigger dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                    <SelectValue placeholder={i18n.language === 'ar' ? 'اختر النوع' : 'Select type'} />
                  </SelectTrigger>
                  <SelectContent dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
                    <SelectItem value="paragraph">{i18n.language === 'ar' ? 'فقرة' : 'Paragraph'}</SelectItem>
                    <SelectItem value="table">{i18n.language === 'ar' ? 'جدول' : 'Table'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/news')}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            <Save className="h-5 w-5 mr-2" />
            {createMutation.isPending || updateMutation.isPending ? 'Saving...' : t('save')}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default NewsForm;
